import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { assertCrewOrAdmin, assertTenantAdmin, defaultTenantId } from "@/lib/tenants.functions";
import { usd } from "@/lib/events";
import {
  normalizeCapabilities,
  pickActiveGateway as pickGateway,
  processingModeFor,
  sanitizedGatewayReason as gatewayReason,
} from "@/lib/payments-core";

export type GatewayCapabilities = {
  card_present: boolean;
  card_not_present: boolean;
  ach: boolean;
};

export type GatewayStatus = {
  id: string | null;
  tenant_id: string;
  tenant_name: string;
  tenant_type: string;
  provider: string;
  environment: "sandbox" | "live";
  merchant_id: string | null;
  fee_profile_id: string | null;
  onboarding_status: string;
  capabilities: GatewayCapabilities;
  is_active: boolean;
  /** Direct merchant of record vs a sub-merchant under the platform. */
  processing_mode: "direct" | "sub_merchant";
  /** Whether the platform Finix credentials are present in secure settings. */
  credentials_connected: boolean;
};

export type CheckoutConfig = {
  /** Public Finix application id — safe in the browser, used to boot hosted fields. */
  applicationId: string | null;
  environment: "sandbox" | "live";
  /** Both platform credentials and an approved gateway are in place. */
  ready: boolean;
  merchantConfigured: boolean;
  /** Admin switch: checkout offers bank (ACH) debit alongside cards. */
  achEnabled: boolean;
  /**
   * Apple Pay / Google Pay buttons. Stays off until the wallet domains are
   * verified on the live account, so no pilot ever taps a button that can't
   * finish.
   */
  walletsEnabled: boolean;
};

// The pure helpers live in payments-core so the charge path can use them
// without importing this server-function module back.
export {
  DEFAULT_CAPABILITIES,
  sanitizedGatewayReason,
  pickActiveGateway,
  processingModeFor,
} from "@/lib/payments-core";

/** Public: what the checkout page needs to boot Finix hosted card fields. */
export const getCheckoutConfig = createServerFn({ method: "GET" }).handler(
  async (): Promise<CheckoutConfig> => {
    const finix = await import("@/lib/finix.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const tenantId = await defaultTenantId(supabaseAdmin);
    const { data } = await supabaseAdmin
      .from("tenant_gateways")
      .select("environment, is_active, onboarding_status, merchant_id, capabilities")
      .eq("tenant_id", tenantId);

    const gateway = pickGateway(data ?? [], finix.defaultEnvironment());
    // The active gateway row decides the environment; hosted fields and the
    // server must then both use that environment's application id.
    const environment =
      (gateway?.environment as "sandbox" | "live" | undefined) ?? finix.defaultEnvironment();
    const { applicationId } = finix.getFinixPublicConfig(environment);
    const merchantConfigured = Boolean(gateway?.merchant_id);

    return {
      applicationId,
      environment,
      merchantConfigured,
      ready: finix.isFinixConfigured(environment) && merchantConfigured,
      walletsEnabled:
        finix.isFinixConfigured(environment) &&
        merchantConfigured &&
        process.env["FINIX_WALLETS_ENABLED"] === "true",
      achEnabled:
        finix.isFinixConfigured(environment) &&
        merchantConfigured &&
        normalizeCapabilities((gateway as Record<string, unknown> | null)?.["capabilities"]).ach,
    };
  },
);

/** Staff view of the current venue's gateway. */
export const getGatewayStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<GatewayStatus> => {
    const finix = await import("@/lib/finix.server");
    const environment = finix.defaultEnvironment();

    const tenantId = await defaultTenantId(context.supabase);

    const { data: tenant } = await context.supabase
      .from("tenants")
      .select("id, name, type")
      .eq("id", tenantId)
      .maybeSingle();

    const { data: rows, error } = await context.supabase
      .from("tenant_gateways")
      .select("*")
      .eq("tenant_id", tenantId);

    if (error) {
      console.error("gateway lookup failed", error);
      throw new Error("Could not load payment settings.");
    }

    const gateway = pickGateway((rows ?? []) as never[], environment) as Record<
      string,
      unknown
    > | null;

    const tenantType = ((tenant as { type?: string } | null)?.type ?? "flagship") as string;

    return {
      id: (gateway?.["id"] as string) ?? null,
      tenant_id: tenantId,
      tenant_name: ((tenant as { name?: string } | null)?.name ?? "This venue") as string,
      tenant_type: tenantType,
      provider: (gateway?.["provider"] as string) ?? "finix",
      environment: ((gateway?.["environment"] as string) ?? environment) as "sandbox" | "live",
      merchant_id: (gateway?.["merchant_id"] as string) ?? null,
      fee_profile_id: (gateway?.["fee_profile_id"] as string) ?? null,
      onboarding_status: (gateway?.["onboarding_status"] as string) ?? "pending",
      capabilities: normalizeCapabilities(gateway?.["capabilities"]),
      is_active: gateway ? gateway["is_active"] !== false : false,
      processing_mode: processingModeFor(tenantType),
      credentials_connected: finix.isFinixConfigured(
        (((gateway?.["environment"] as string) ?? environment) === "live" ? "live" : "sandbox") as
          "sandbox" | "live",
      ),
    };
  });

/** Admin: switch the venue between test and live processing. */
export const updateGatewayEnvironment = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z.object({ environment: z.enum(["sandbox", "live"]) }).parse(data),
  )
  .handler(async ({ context, data }) => {
    await assertTenantAdmin(context);
    const tenantId = await defaultTenantId(context.supabase);

    // Guardrail: never flip the venue to live charges unless live credentials
    // and an approved live merchant are actually in place.
    if (data.environment === "live") {
      const finix = await import("@/lib/finix.server");
      const missing: string[] = [];
      if (!finix.isFinixConfigured("live")) missing.push("live provider credentials");
      const { data: liveRow } = await context.supabase
        .from("tenant_gateways")
        .select("merchant_id")
        .eq("tenant_id", tenantId)
        .eq("provider", "finix")
        .eq("environment", "live")
        .maybeSingle();
      if (!liveRow?.merchant_id) missing.push("an approved live merchant account");
      if (missing.length > 0) {
        throw new Error(`Live payments aren't ready yet — still missing ${missing.join(" and ")}.`);
      }
    }

    const { error } = await context.supabase.from("tenant_gateways").upsert(
      {
        tenant_id: tenantId,
        provider: "finix",
        environment: data.environment,
        is_active: true,
      } as never,
      { onConflict: "tenant_id,provider,environment" },
    );

    if (error) {
      console.error("gateway environment update failed", error);
      throw new Error("Could not update the payment environment.");
    }

    // Only one environment is live at a time for a venue.
    const { error: deactivateError } = await context.supabase
      .from("tenant_gateways")
      .update({ is_active: false } as never)
      .eq("tenant_id", tenantId)
      .neq("environment", data.environment);
    if (deactivateError) console.error("gateway deactivate failed", deactivateError);

    return { ok: true, environment: data.environment };
  });

const LINE_ITEM = z.object({
  label: z.string().trim().min(1).max(120),
  amount_cents: z.number().int().min(0).max(5_000_000),
  quantity: z.number().int().min(1).max(50),
});

const PAYMENT_INTENT_INPUT = z.object({
  /** Token minted in the browser by Finix hosted fields. */
  finix_token: z.string().trim().min(6).max(120),
  /** What the browser tokenized — bank tokens are reported by Finix's form. */
  instrument_type: z.enum(["PAYMENT_CARD", "BANK_ACCOUNT"]).optional(),
  amount_cents: z.number().int().min(50).max(5_000_000),
  currency: z.string().trim().length(3).default("USD"),
  idempotency_key: z.string().trim().min(8).max(120),
  buyer_email: z.string().trim().email().max(200).optional(),
  buyer_name: z.string().trim().min(1).max(120).optional(),
  items: z.array(LINE_ITEM).max(30).default([]),
});

export type PaymentIntentResult = {
  transaction_id: string;
  status: string;
  card_brand: string | null;
  last_four: string | null;
  failure_message: string | null;
};

// Casual-abuse throttle. Workers are stateless, so this is per-instance only.
const CHECKOUT_WINDOW_MS = 60_000;
const CHECKOUT_MAX_PER_WINDOW = 8;
const checkoutHits = new Map<string, number[]>();

function clientIp(): string {
  const req = getRequest();
  const forwarded = req.headers.get("x-forwarded-for");
  if (forwarded) return forwarded.split(",")[0]?.trim() ?? "unknown";
  return req.headers.get("cf-connecting-ip") ?? req.headers.get("x-real-ip") ?? "unknown";
}

function throttled(ip: string): boolean {
  const now = Date.now();
  const recent = (checkoutHits.get(ip) ?? []).filter((t) => now - t < CHECKOUT_WINDOW_MS);
  if (recent.length >= CHECKOUT_MAX_PER_WINDOW) {
    checkoutHits.set(ip, recent);
    return true;
  }
  recent.push(now);
  checkoutHits.set(ip, recent);
  return false;
}

/**
 * Public card-not-present checkout — guests can buy passes and gear without an
 * account. The transaction row is written first (under the caller's idempotency
 * key), then updated with Finix's synchronous answer so the confirmation screen
 * never has to wait on webhook latency.
 */
export const createPaymentIntent = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => PAYMENT_INTENT_INPUT.parse(data))
  .handler(async ({ data }): Promise<PaymentIntentResult> => {
    if (throttled(clientIp())) throw new Error("Too many attempts. Please wait a minute.");
    const { chargeWithToken } = await import("@/lib/finix-charge.server");
    return chargeWithToken({
      finix_token: data.finix_token,
      ...(data.instrument_type ? { instrument_type: data.instrument_type } : {}),
      amount_cents: data.amount_cents,
      currency: data.currency,
      idempotency_key: data.idempotency_key,
      buyer_email: data.buyer_email,
      buyer_name: data.buyer_name,
      items: data.items,
    });
  });

export type PublicTransaction = {
  id: string;
  status: string;
  amount_cents: number;
  currency: string;
  card_brand: string | null;
  last_four: string | null;
  created_at: string;
  failure_message: string | null;
  items: { label: string; amount_cents: number; quantity: number }[];
  /** "card" or "bank_account" — drives the required ACH confirmation language. */
  payment_method: string | null;
};

/**
 * Public receipt lookup by unguessable transaction id, for the confirmation page.
 * Receipts stop being publicly readable after RECEIPT_TTL_HOURS unless the
 * caller proves they are the buyer by supplying the matching email.
 */
export const RECEIPT_TTL_HOURS = 72;

/** True when a receipt may be shown for this row. */
export function receiptIsViewable(args: {
  createdAt: string | null | undefined;
  buyerEmail: string | null | undefined;
  suppliedEmail: string | null | undefined;
  now?: Date;
}): boolean {
  const created = args.createdAt ? Date.parse(args.createdAt) : NaN;
  const now = (args.now ?? new Date()).getTime();
  const fresh =
    Number.isFinite(created) &&
    now - created <= RECEIPT_TTL_HOURS * 60 * 60 * 1000 &&
    created <= now + 60_000;
  if (fresh) return true;
  const buyer = (args.buyerEmail ?? "").trim().toLowerCase();
  const supplied = (args.suppliedEmail ?? "").trim().toLowerCase();
  return buyer.length > 0 && buyer === supplied;
}

export const getTransaction = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid(), email: z.string().trim().max(200).optional() }).parse(data),
  )
  .handler(async ({ data }): Promise<PublicTransaction | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("transactions")
      .select("id, status, amount_cents, currency, card_brand, last_four, created_at, metadata")
      .eq("id", data.id)
      .maybeSingle();

    if (error) {
      console.error("transaction lookup failed", error);
      throw new Error("Could not load that payment.");
    }
    if (!row) return null;

    {
      const r = row as Record<string, unknown>;
      const m = (r["metadata"] ?? {}) as Record<string, unknown>;
      const viewable = receiptIsViewable({
        createdAt: r["created_at"] as string,
        buyerEmail: (m["buyer_email"] as string) ?? null,
        suppliedEmail: data.email ?? null,
      });
      if (!viewable) return null;
    }

    const record = row as Record<string, unknown>;
    const meta = (record["metadata"] ?? {}) as Record<string, unknown>;

    return {
      id: record["id"] as string,
      status: record["status"] as string,
      amount_cents: Number(record["amount_cents"] ?? 0),
      currency: (record["currency"] as string) ?? "USD",
      card_brand: (record["card_brand"] as string) ?? null,
      last_four: (record["last_four"] as string) ?? null,
      created_at: record["created_at"] as string,
      failure_message: (meta["failure_message"] as string) ?? null,
      payment_method: (meta["method"] as string) ?? null,
      items: Array.isArray(meta["items"]) ? (meta["items"] as PublicTransaction["items"]) : [],
    };
  });

/** Crew view: recent card/bank transactions for refunds and reconciliation. */
export type RecentTransaction = {
  id: string;
  status: string;
  amount_cents: number;
  currency: string;
  card_brand: string | null;
  last_four: string | null;
  created_at: string;
  buyer_email: string | null;
  failure_message: string | null;
};

export const listRecentTransactions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ transactions: RecentTransaction[] }> => {
    await assertCrewOrAdmin(context);

    // PCI: the transactions table is invisible to application roles by RLS —
    // only the service role reads it, and only after the caller cleared the
    // crew/admin check above.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("transactions")
      .select("id, status, amount_cents, currency, card_brand, last_four, created_at, metadata")
      .order("created_at", { ascending: false })
      .limit(25);

    if (error) {
      console.error("recent transactions lookup failed", error);
      throw new Error("Could not load recent payments.");
    }

    const transactions = ((data ?? []) as Record<string, unknown>[]).map((row) => {
      const meta = (row["metadata"] ?? {}) as Record<string, unknown>;
      return {
        id: row["id"] as string,
        status: row["status"] as string,
        amount_cents: Number(row["amount_cents"] ?? 0),
        currency: ((row["currency"] as string) ?? "USD").toUpperCase(),
        card_brand: (row["card_brand"] as string) ?? null,
        last_four: (row["last_four"] as string) ?? null,
        created_at: row["created_at"] as string,
        buyer_email: (meta["buyer_email"] as string) ?? null,
        failure_message: (meta["failure_message"] as string) ?? null,
      };
    });

    return { transactions };
  });

/** Admin: switch a payment capability (e.g. ACH bank transfers) on or off. */
export const updateGatewayCapabilities = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ ach: z.boolean() }).parse(data))
  .handler(async ({ context, data }) => {
    await assertTenantAdmin(context);

    const tenantId = await defaultTenantId(context.supabase);
    const { defaultEnvironment } = await import("@/lib/finix.server");
    const environment = defaultEnvironment();

    const { data: rows, error } = await context.supabase
      .from("tenant_gateways")
      .select("id, environment, is_active, onboarding_status, merchant_id, capabilities")
      .eq("tenant_id", tenantId);

    if (error || !rows || rows.length === 0) {
      throw new Error("Could not load payment settings.");
    }

    const gateway = pickGateway(
      (rows ?? []) as {
        id: string;
        environment: string;
        is_active: boolean;
        onboarding_status: string;
        merchant_id: string | null;
        capabilities: unknown;
      }[],
      environment,
    );
    if (!gateway?.id) throw new Error("No active payment gateway to update.");

    const next = { ...normalizeCapabilities(gateway.capabilities), ach: data.ach };

    const { error: updateError } = await context.supabase
      .from("tenant_gateways")
      .update({ capabilities: next } as never)
      .eq("id", gateway.id);

    if (updateError) {
      console.error("gateway capabilities update failed", updateError);
      throw new Error("Could not update payment capabilities.");
    }

    return { ok: true, capabilities: next };
  });

/**
 * Crew/admin: refund a captured card payment (full or partial) by creating a
 * Finix REVERSAL transfer. The webhook moves the row to `refunded` when
 * Finix settles the reversal.
 */
export const refundTransaction = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        transaction_id: z.string().uuid(),
        amount_cents: z.number().int().positive().max(1_000_000),
        reason: z.string().trim().max(300).optional(),
      })
      .parse(data),
  )
  .handler(async ({ context, data }) => {
    await assertCrewOrAdmin(context);

    // PCI: service-role read only, after the crew/admin check.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("transactions")
      .select("id, status, amount_cents, currency, finix_transfer_id, metadata")
      .eq("id", data.transaction_id)
      .maybeSingle();

    if (error || !row) throw new Error("Could not load that payment.");

    const tx = row as Record<string, unknown>;
    const originalTransferId = tx["finix_transfer_id"] as string | null;
    if (!originalTransferId) throw new Error("That payment has no provider reference to refund.");
    if (tx["status"] !== "captured") {
      throw new Error(
        tx["status"] === "refunded"
          ? "That payment was already refunded."
          : `Only captured payments can be refunded (this one is ${tx["status"]}).`,
      );
    }
    const charged = Number(tx["amount_cents"] ?? 0);
    if (data.amount_cents > charged) {
      throw new Error(`Refund can't be more than the ${usd(charged)} charged.`);
    }

    const meta = (tx["metadata"] ?? {}) as Record<string, unknown>;

    const finix = await import("@/lib/finix.server");
    // Refund through the environment the original charge ran in.
    const environment =
      (meta["environment"] as "sandbox" | "live" | undefined) ?? finix.defaultEnvironment();
    const reversal = await finix
      .createRefundTransfer({
        originalTransferId,
        refundAmountCents: data.amount_cents,
        idempotencyKey: `refund-${data.transaction_id}-${data.amount_cents}`,
        tags: { transaction_id: data.transaction_id },
        environment,
      })
      .catch((e: unknown) => {
        console.error("finix refund failed", e);
        throw new Error(
          e instanceof Error && e.message ? e.message : "The provider declined the refund.",
        );
      });

    const refundMeta = {
      ...meta,
      finix_refund_transfer_id: reversal.id,
      refund_state: reversal.state ?? null,
      refund_amount_cents: data.amount_cents,
      refund_reason: data.reason ?? null,
      refunded_at: new Date().toISOString(),
    };

    const refundStatus = finix.transferStateToStatus(reversal.state);
    const { error: updateError } = await supabaseAdmin
      .from("transactions")
      .update({
        // PENDING reversal: the webhook advances this to `refunded`.
        status: refundStatus === "refunded" ? "refunded" : tx["status"],
        metadata: refundMeta as never,
      } as never)
      .eq("id", data.transaction_id);

    if (updateError) console.error("refund transaction update failed", updateError);

    return {
      ok: true,
      message:
        refundStatus === "refunded"
          ? `Refunded ${usd(data.amount_cents)}.`
          : `Refund of ${usd(data.amount_cents)} is processing — it'll show as refunded shortly.`,
    };
  });
